import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import {
  Calendar,
  Wallet,
  CreditCard,
  AlertCircle,
  X,
  User,
  Phone,
  Building2,
  CheckCircle2,
  CreditCard as CreditCardIcon,
  ArrowLeftRight,
} from "lucide-react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ProductSection from "../ProductsModule/Components/ProductSection";
import FirmDetailsCard from "./Components/AddReceiptComponents/FirmDetailsCard";
import OutstandingBillsTable from "./Components/AddReceiptComponents/OutstandingBillsTable";
import AddReceiptTable from "./Components/AddReceiptTable";
import { dummyFirms, dummyOutstandingBills } from "../../../dummyData";

const AddReceipt = () => {
  // Form state
  const [receiptDate, setReceiptDate] = useState(dayjs());
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [notes, setNotes] = useState("");
  const [adjustedBills, setAdjustedBills] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [excessAmountHandling, setExcessAmountHandling] = useState("credit"); // 'credit' or 'refund'
  const [excessAmount, setExcessAmount] = useState(0);
  const [excessAmountNotes, setExcessAmountNotes] = useState("");

  // Get outstanding bills for selected firm
  const outstandingBills = selectedFirm
    ? dummyOutstandingBills[selectedFirm.id] || []
    : [];

  // Calculate total adjusted amount and check for excess
  const calculateExcessAmount = () => {
    if (!selectedFirm || !adjustedBills.length || !amount) return 0;

    const totalReceiptAmount = parseFloat(amount) || 0;
    const totalAdjusted = adjustedBills.reduce((sum, bill) => {
      return sum + (parseFloat(bill.adjustedAmount) || 0);
    }, 0);

    return Math.max(0, totalReceiptAmount - totalAdjusted);
  };

  // Update excess amount when relevant data changes
  useEffect(() => {
    const excess = calculateExcessAmount();
    setExcessAmount(excess);
  }, [amount, adjustedBills]);

  const handleFirmChange = (event, newValue) => {
    setSelectedFirm(newValue);
  };

  const handleAmountChange = event => {
    setAmount(event.target.value);
  };

  const handlePaymentModeChange = event => {
    setPaymentMode(event.target.value);
  };

  const handleAdjustedBillsChange = billsWithAdjustments => {
    setAdjustedBills(billsWithAdjustments);
  };

  const handleExcessAmountHandlingChange = event => {
    setExcessAmountHandling(event.target.value);
  };

  const handleSave = () => {
    // In a real app, this would save the receipt to the backend
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      console.log({
        receiptDate: receiptDate.format("YYYY-MM-DD"),
        firmId: selectedFirm?.id,
        amount: parseFloat(amount),
        paymentMode,
        notes,
        adjustedBills,
        excessAmount: excessAmount > 0 ? excessAmount : null,
        excessAmountHandling: excessAmount > 0 ? excessAmountHandling : null,
        excessAmountNotes: excessAmount > 0 ? excessAmountNotes : null,
      });

      setIsSubmitting(false);
      // Show success modal instead of alert
      setShowSuccessModal(true);
    }, 1000);
  };

  const handleCancel = () => {
    // Open the confirmation dialog instead of using global confirm
    setShowCancelDialog(true);
  };

  const handleCancelConfirmed = () => {
    // Reset form when cancel is confirmed
    setSelectedFirm(null);
    setAmount("");
    setPaymentMode("cash");
    setNotes("");
    setAdjustedBills([]);
    setReceiptDate(dayjs());
    setExcessAmountHandling("credit");
    setExcessAmount(0);
    setExcessAmountNotes("");
    setShowCancelDialog(false);
  };

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Optional: Reset form after success
    handleCancelConfirmed();
  };

  // Validate form
  const isFormValid = () => {
    return selectedFirm && amount && parseFloat(amount) > 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold font-outfit text-gray-800">
              Add Receipt
            </h1>
          </div>
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-6 py-6">
        {/* Main form section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Autocomplete
                id="firm-select"
                options={dummyFirms}
                getOptionLabel={option => option.firmName || ""}
                value={selectedFirm}
                onChange={handleFirmChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Firm"
                    variant="outlined"
                    required
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Wallet size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#E5E7EB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#5652B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                    }}
                  />
                )}
              />

              <TextField
                label="Amount"
                variant="outlined"
                required
                size="small"
                type="number"
                value={amount}
                onChange={handleAmountChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="text-[#5652B0]">₹</span>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Receipt Date"
                  value={receiptDate}
                  onChange={newDate => setReceiptDate(newDate)}
                  slotProps={{
                    textField: {
                      size: "small",
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#E5E7EB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#5652B0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>

        {/* Firm details container - Updated to show requested fields */}
        {selectedFirm && (
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
                Firm Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-md">
                    <Building2 size={20} className="text-[#5652B0]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">
                      Firm Name
                    </p>
                    <p className="font-medium text-gray-800">
                      {selectedFirm.firmName}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-md">
                    <User size={20} className="text-[#5652B0]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Owner</p>
                    <p className="font-medium text-gray-800">
                      {selectedFirm.ownerName}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-md">
                    <Phone size={20} className="text-[#5652B0]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Phone</p>
                    <p className="font-medium text-gray-800">
                      {selectedFirm.phoneNumber}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:col-span-2">
                  <div className="bg-purple-50 p-2 rounded-md">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#5652B0]">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Address</p>
                    <p className="font-medium text-gray-800">
                      {selectedFirm.address ? (
                        <>
                          {selectedFirm.address}
                          {selectedFirm.city && selectedFirm.state && (
                            <span>
                              , {selectedFirm.city}, {selectedFirm.state}
                            </span>
                          )}
                        </>
                      ) : (
                        "Not Available"
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-md">
                    <AlertCircle size={20} className="text-[#5652B0]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">GSTIN</p>
                    <p className="font-medium text-gray-800">
                      {selectedFirm.gstNumber || "Not Available"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment mode section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
              Payment Mode
            </h2>
            <div className="py-2">
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="payment-mode"
                  value={paymentMode}
                  onChange={handlePaymentModeChange}>
                  <FormControlLabel
                    value="cash"
                    control={
                      <Radio
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="flex items-center font-outfit">
                        <Wallet size={18} className="mr-2" />
                        Cash
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="bank"
                    control={
                      <Radio
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="flex items-center font-outfit">
                        <CreditCard size={18} className="mr-2" />
                        Bank
                      </span>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </div>

        {/* Bill adjustment section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            {selectedFirm ? (
              <>
                <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
                  Adjust in Outstanding Bills
                </h2>
                <div className="py-2">
                  <OutstandingBillsTable
                    bills={outstandingBills}
                    totalReceiptAmount={parseFloat(amount) || 0}
                    onAdjustedAmountsChange={handleAdjustedBillsChange}
                  />
                </div>

                {/* Excess Amount Section */}
                {excessAmount > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-medium text-gray-800">
                        Excess Amount
                      </h3>
                      <div className="bg-blue-50 px-3 py-1 rounded-full">
                        <span className="text-blue-700 font-medium">
                          ₹ {excessAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <div className="flex items-start">
                        <AlertCircle
                          size={20}
                          className="text-blue-600 mt-0.5 mr-2"
                        />
                        <p className="text-blue-700 text-sm">
                          The receipt amount exceeds the total bill amount by ₹
                          {excessAmount.toFixed(2)}. Please specify how to
                          handle this excess amount.
                        </p>
                      </div>
                    </div>

                    <FormControl component="fieldset" className="mb-4 w-full">
                      <p className="text-sm text-gray-600 mb-2 font-medium">
                        Handle Excess Amount
                      </p>
                      <RadioGroup
                        row
                        name="excess-amount-handling"
                        value={excessAmountHandling}
                        onChange={handleExcessAmountHandlingChange}>
                        <FormControlLabel
                          value="credit"
                          control={
                            <Radio
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": {
                                  color: "#5652B0",
                                },
                              }}
                            />
                          }
                          label={
                            <span className="flex items-center font-outfit">
                              <CreditCardIcon size={18} className="mr-2" />
                              Add as Account Credit
                            </span>
                          }
                        />
                        <FormControlLabel
                          value="refund"
                          control={
                            <Radio
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": {
                                  color: "#5652B0",
                                },
                              }}
                            />
                          }
                          label={
                            <span className="flex items-center font-outfit">
                              <ArrowLeftRight size={18} className="mr-2" />
                              Process Refund
                            </span>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder={
                        excessAmountHandling === "credit"
                          ? "Add notes about the account credit..."
                          : "Add refund details like bank account, UPI ID, or other payment information..."
                      }
                      value={excessAmountNotes}
                      onChange={e => setExcessAmountNotes(e.target.value)}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#E5E7EB",
                          },
                          "&:hover fieldset": {
                            borderColor: "#5652B0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
                  Adjustment in Bills
                </h2>
                <div className="py-2">
                  <AddReceiptTable onBillsChange={setAdjustedBills} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Notes section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
              Add Note
            </h2>
            <div className="py-2">
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add any additional notes or references here..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 flex justify-end space-x-4">
            <button
              onClick={handleCancel}
              className="px-6 py-2.5 rounded-lg text-base font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid() || isSubmitting}
              className={`px-6 py-2.5 rounded-lg text-base font-medium text-white transition-all flex items-center ${
                isFormValid() && !isSubmitting
                  ? "bg-[#5652B0] hover:bg-[#4A47A3]"
                  : "bg-gray-300 cursor-not-allowed"
              }`}>
              {isSubmitting ? (
                <>
                  <CircularProgress
                    size={20}
                    color="inherit"
                    className="mr-2"
                  />
                  Saving...
                </>
              ) : (
                "Save Receipt"
              )}
            </button>
          </div>
        </div>

        {/* Cancel Confirmation Dialog */}
        <Dialog
          open={showCancelDialog}
          onClose={handleCancelDialogClose}
          aria-labelledby="cancel-dialog-title"
          aria-describedby="cancel-dialog-description"
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle
            id="cancel-dialog-title"
            sx={{ borderBottom: "1px solid #E5E7EB" }}>
            <div className="flex justify-between items-center">
              <span className="font-outfit">Cancel Receipt Entry?</span>
              <button
                onClick={handleCancelDialogClose}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                <X size={18} />
              </button>
            </div>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText
              id="cancel-dialog-description"
              className="font-outfit text-gray-600">
              Are you sure you want to cancel? Any unsaved changes will be lost.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCancelDialogClose}
              variant="outlined"
              sx={{
                borderColor: "#E5E7EB",
                color: "#374151",
                "&:hover": {
                  borderColor: "#D1D5DB",
                  backgroundColor: "#F9FAFB",
                },
                textTransform: "none",
                fontFamily: "Outfit, sans-serif",
              }}>
              Keep Editing
            </Button>
            <Button
              onClick={handleCancelConfirmed}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
                textTransform: "none",
                fontFamily: "Outfit, sans-serif",
              }}>
              Yes, Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Modal */}
        <Dialog
          open={showSuccessModal}
          onClose={handleSuccessModalClose}
          aria-labelledby="success-dialog-title"
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
              maxWidth: "400px",
            },
          }}>
          <div className="p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
            </div>
            <h2
              id="success-dialog-title"
              className="text-xl font-semibold mb-2 font-outfit text-gray-800">
              Receipt Saved Successfully!
            </h2>
            <p className="text-gray-600 mb-6 font-outfit">
              Your receipt has been processed and saved successfully.
              {excessAmount > 0 && (
                <>
                  <br />
                  <br />
                  <span className="font-medium">
                    Excess amount of ₹{excessAmount.toFixed(2)} has been{" "}
                    {excessAmountHandling === "credit"
                      ? "added as account credit"
                      : "marked for refund"}
                    .
                  </span>
                </>
              )}
            </p>
            <Button
              onClick={handleSuccessModalClose}
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
                textTransform: "none",
                fontFamily: "Outfit, sans-serif",
                padding: "10px 16px",
                fontSize: "16px",
              }}>
              Continue
            </Button>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default AddReceipt;
